import React from "react";
import type { LucideIcon } from "lucide-react";
import { DateHelper } from "@bryntum/schedulerpro";
import { Badge } from "@/modern/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { UserAvatar } from "@/modern/components/user-avatar";
import { cn } from "@/modern/utils";
import { ENTITY_TYPES, TASK_BUDGET_FIELDS } from "@/other/constants/OtherKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { budgetCurrencyFormat } from "@/other/utils/budgetUtils";
import { getBudgetFieldsConfig } from "@/other/constants/TaskBudget/taskBudgetViewColumnsData";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { getFontAwesomeClassName } from "@/other/utils/utils.jsx";
import type { KanbanCardFieldDefinition } from "@/modern/constants/kanbanCardFieldCatalog";

type EntityTypeValue = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

type BoardUserLike = {
  id: number | string;
  name?: string;
  profileImageURL?: string;
  archived?: boolean;
};

let kanbanCardFieldCatalogMap: Partial<Record<EntityTypeValue, KanbanCardFieldDefinition[]>> | null = null;
let maxKanbanVisibleFields = 4;

export const registerKanbanCardFieldCatalog = (
  catalog: Partial<Record<EntityTypeValue, KanbanCardFieldDefinition[]>>,
  maxVisibleFields: number
) => {
  kanbanCardFieldCatalogMap = catalog;
  maxKanbanVisibleFields = maxVisibleFields;
};

const getCatalogMap = () => {
  if (!kanbanCardFieldCatalogMap) {
    throw new Error("Kanban card field catalog has not been registered.");
  }
  return kanbanCardFieldCatalogMap;
};

export type KanbanFieldValueSource = {
  id: string;
  label?: string;
  icon?: LucideIcon;
  recordField?: string;
  resolveValue?: (ctx: KanbanCardFieldRenderContext) => unknown;
};

export type KanbanCardFieldRenderContext = {
  project: any;
  record: any;
  userPreferences: {
    getSetting: (key: string, defaultValue?: string, isBoardPref?: boolean) => string | undefined;
  } | null;
  entityType: string;
  grouperField?: string;
  field?: KanbanFieldValueSource;
};

const getNestedRecordValue = (record: unknown, path: string): unknown =>
  path.split(".").reduce((obj: any, key) => obj?.[key], record);

export const setNestedRecordValue = (record: Record<string, unknown>, path: string, value: unknown) => {
  const keys = path.split(".");
  let target = record;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in target) || typeof target[key] !== "object" || target[key] === null) {
      target[key] = {};
    }
    target = target[key] as Record<string, unknown>;
  }
  target[keys[keys.length - 1]] = value;
};

const hasDisplayValue = (v: unknown) =>
  v != null && v !== "" && !(Array.isArray(v) && v.length === 0);

const KANBAN_CARD_TITLE_CLASS =
  "m-0 text-sm font-medium leading-snug text-foreground line-clamp-2";
const KANBAN_CARD_BODY_TEXT_CLASS = "text-sm leading-snug";
const KANBAN_CARD_BODY_CLASS = `${KANBAN_CARD_BODY_TEXT_CLASS} text-muted-foreground`;
const KANBAN_CARD_BADGE_CLASS =
  "text-sm px-1.5 py-0.5 rounded-md truncate text-start min-w-0 inline-block";
const KANBAN_CARD_ICON_CLASS = "size-4 shrink-0 text-primary";

const resolveKanbanFieldValue = (ctx: KanbanCardFieldRenderContext, field: KanbanFieldValueSource): unknown => {
  const path = field.recordField ?? field.id;
  const fromRecord = getNestedRecordValue(ctx.record, path);
  if (hasDisplayValue(fromRecord)) return fromRecord;
  return field.resolveValue?.(ctx);
};

const fieldValue = (ctx: KanbanCardFieldRenderContext) => resolveKanbanFieldValue(ctx, ctx.field!);

const fieldLabel = (ctx: KanbanCardFieldRenderContext) => ctx.field?.label ?? "";

const fieldIcon = (ctx: KanbanCardFieldRenderContext) => ctx.field?.icon;

function KanbanFieldIconRow({
  ctx,
  children,
  className,
  iconAlign = "center",
}: {
  ctx: KanbanCardFieldRenderContext;
  children: React.ReactNode;
  className?: string;
  iconAlign?: "start" | "center";
}) {
  const Icon = fieldIcon(ctx);
  const label = fieldLabel(ctx);

  const content = (
    <div
      className={cn(
        "flex min-w-0 gap-1.5",
        iconAlign === "center" ? "items-center" : "items-start",
        className
      )}
    >
      {Icon ? (
        <Icon
          className={cn(
            KANBAN_CARD_ICON_CLASS,
            iconAlign === "start" && "mt-0.5"
          )}
          aria-hidden
        />
      ) : null}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );

  if (!label) return content;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span className="inline-flex min-w-0 max-w-full cursor-default">{content}</span>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

const renderIconValueLine = (
  ctx: KanbanCardFieldRenderContext,
  value: string,
  className?: string
) => {
  if (!value) return null;
  return (
    <KanbanFieldIconRow ctx={ctx}>
      <span className={cn("block min-w-0", KANBAN_CARD_BODY_CLASS, className ?? "truncate")}>
        {value}
      </span>
    </KanbanFieldIconRow>
  );
};

const getBudgetRecord = (ctx: KanbanCardFieldRenderContext) => {
  const budgetId = ctx.record?.budgetId ?? ctx.record?.budget;
  if (!budgetId && !ctx.record?.budgetRecord) return ctx.record?.budgetRecord ?? null;
  if (!ctx.project) return ctx.record?.budgetRecord ?? null;
  return (
    ctx.project.getCrudStore?.(STORE_KEYS.taskBudgetStore)?.getById?.(budgetId) ?? ctx.record?.budgetRecord ?? null
  );
};

export const getTaskBoardUsers = (ctx: KanbanCardFieldRenderContext): BoardUserLike[] => {
  const { project, record } = ctx;
  if (!project || !record || ctx.entityType !== ENTITY_TYPES.TASK) return [];

  const taskEvents = project.eventStore?.query?.((eventRecord: any) => eventRecord.taskId === record.id, true);
  if (!taskEvents?.length) return [];

  const boardUsersMap = new Map<number | string, BoardUserLike>();
  taskEvents.forEach((event: any) => {
    const resource = event.resource;
    resource?._assignedBoardUsers?.forEach((boardUser: any) => {
      if (boardUser && !boardUsersMap.has(boardUser.id)) {
        boardUsersMap.set(boardUser.id, {
          id: boardUser.id,
          name: boardUser.name,
          profileImageURL: boardUser.profileImageURL,
          archived: boardUser.archived,
        });
      }
    });
  });
  return Array.from(boardUsersMap.values());
};

export const resolveContractAmountValue = (ctx: KanbanCardFieldRenderContext) =>
  getBudgetRecord(ctx)?.[TASK_BUDGET_FIELDS.contractAmount];

export const resolveBudgetLabelValue = (ctx: KanbanCardFieldRenderContext) => {
  const pref = ctx.userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.budgetLabel, "notDisplayed", true);
  if (!pref || pref === "notDisplayed") return null;
  const config = getBudgetFieldsConfig().find((f) => f.budgetField === pref);
  const budget = getBudgetRecord(ctx);
  return config && budget ? budget[config.budgetField] : null;
};

export const resolveCreatorName = (ctx: KanbanCardFieldRenderContext) =>
  ctx.record?.creatorRecord?.name ?? null;

export const resolveTaskLocation = (ctx: KanbanCardFieldRenderContext) =>
  ctx.record?.locationValue ?? ctx.record?.location?.locationValue ?? null;

export const resolveAssignedBoardUsers = (ctx: KanbanCardFieldRenderContext): BoardUserLike[] | null => {
  const assigned = ctx.record?._assignedBoardUsers;
  if (Array.isArray(assigned) && assigned.length) return assigned;

  const ids = ctx.record?.assignedBoardUsers;
  if (!Array.isArray(ids) || !ids.length || !ctx.project?.boardUsersStore) return null;

  return ids
    .map((id: number | string) => ctx.project.boardUsersStore.getById(id))
    .filter(Boolean)
    .map((user: any) => ({
      id: user.id,
      name: user.name,
      profileImageURL: user.profileImageURL,
      archived: user.archived,
    }));
};

export const resolveEligibleBoardUsers = (ctx: KanbanCardFieldRenderContext): BoardUserLike[] | null => {
  const eligible = ctx.record?.eligibleBoardUserRecords;
  if (Array.isArray(eligible) && eligible.length) {
    return eligible.map((user: any) => ({
      id: user.id,
      name: user.name,
      profileImageURL: user.profileImageURL,
      archived: user.archived,
    }));
  }

  const ids = ctx.record?.eligibleBoardUsers;
  if (!Array.isArray(ids) || !ids.length || !ctx.project?.boardUsersStore) return null;

  return ids
    .map((id: number | string) => ctx.project.boardUsersStore.getById(id))
    .filter(Boolean)
    .map((user: any) => ({
      id: user.id,
      name: user.name,
      profileImageURL: user.profileImageURL,
      archived: user.archived,
    }));
};

const BADGE_CLS = KANBAN_CARD_BADGE_CLASS;

const formatKanbanDate = (value: unknown) => {
  if (!value) return null;
  try {
    return DateHelper.format(new Date(value as string | Date), "MM/DD/YYYY");
  } catch {
    return null;
  }
};

const renderTagBadges = (value: unknown) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
        {value.slice(0, 2).map((v, i) => (
          <Badge key={i} variant="accent" size="sm" className={BADGE_CLS}>
            {v}
          </Badge>
        ))}
        {value.length > 2 && (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <span className={cn(KANBAN_CARD_BODY_CLASS, "shrink-0 cursor-default")}>+{value.length - 2}</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <ul className={cn(KANBAN_CARD_BODY_CLASS, "list-disc list-inside space-y-0.5")}>
                {value.slice(2).map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  }
  if (value == null || value === "") return null;
  return (
    <Badge variant="accent" size="sm" className={BADGE_CLS}>
      {String(value)}
    </Badge>
  );
};

const renderTextLine = (
  ctx: KanbanCardFieldRenderContext,
  text: string | null | undefined,
  className?: string
) => {
  if (!text) return null;
  return (
    <KanbanFieldIconRow ctx={ctx} iconAlign="start">
      <p className={cn("m-0 line-clamp-2", KANBAN_CARD_BODY_CLASS, className)}>{text}</p>
    </KanbanFieldIconRow>
  );
};

export const renderColorStrip = (ctx: KanbanCardFieldRenderContext) => {
  const color = fieldValue(ctx);
  if (color == null) return null;
  return (
    <div
      style={{
        width: "0.25rem",
        backgroundColor: color as string,
        position: "absolute",
        left: "-2.5rem",
        top: "-1rem",
        bottom: "-1rem",
      }}
    />
  );
};

export const renderTitle = (ctx: KanbanCardFieldRenderContext) => {
  const title = fieldValue(ctx) as string | undefined;
  if (!title) return null;
  return (
    <div className="flex items-start justify-between gap-2">
      <h4 className={cn(KANBAN_CARD_TITLE_CLASS, ctx.record?.archived && "line-through")}>
        {title}
      </h4>
    </div>
  );
};

export const renderText =
  (className?: string) =>
  (ctx: KanbanCardFieldRenderContext) =>
    renderTextLine(ctx, fieldValue(ctx) as string, className);

export const renderLabeledText =
  (className?: string) =>
  (ctx: KanbanCardFieldRenderContext) =>
    renderIconValueLine(ctx, fieldValue(ctx) as string, className);

export const renderLabeledDate = (ctx: KanbanCardFieldRenderContext) => {
  const formatted = formatKanbanDate(fieldValue(ctx));
  if (!formatted) return null;
  return renderIconValueLine(ctx, formatted);
};

export const renderTags = (ctx: KanbanCardFieldRenderContext) => {
  const badges = renderTagBadges(fieldValue(ctx));
  if (!badges) return null;
  return <KanbanFieldIconRow ctx={ctx}>{badges}</KanbanFieldIconRow>;
};

export const renderBoardUsers = (ctx: KanbanCardFieldRenderContext) => {
  const boardUsers = fieldValue(ctx) as BoardUserLike[];
  if (!Array.isArray(boardUsers) || !boardUsers.length) return null;
  return (
    <KanbanFieldIconRow ctx={ctx}>
      <div className="flex items-center -space-x-1.5 shrink-0">
        {boardUsers.slice(0, 3).map((user) => (
          <div key={user.id} className="transition-transform hover:scale-110 hover:z-10">
            <UserAvatar user={user as any} size="small" showTooltip avatarClassName="ring-2 ring-card cursor-default" />
          </div>
        ))}
        {boardUsers.length > 3 && (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium text-foreground ring-2 ring-card">
            +{boardUsers.length - 3}
          </div>
        )}
      </div>
    </KanbanFieldIconRow>
  );
};

/** @deprecated Use renderBoardUsers */
export const renderAssignees = renderBoardUsers;

export const renderBudgetAmount = (ctx: KanbanCardFieldRenderContext) => {
  const amount = fieldValue(ctx);
  if (amount == null || amount === "") return null;
  return (
    <KanbanFieldIconRow ctx={ctx}>
      <p className={cn("m-0 font-medium text-foreground tabular-nums", KANBAN_CARD_BODY_TEXT_CLASS)}>
        {budgetCurrencyFormat.format(amount as number)}
      </p>
    </KanbanFieldIconRow>
  );
};

export const renderBudgetLabel = (ctx: KanbanCardFieldRenderContext) => {
  const pref = ctx.userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.budgetLabel, "notDisplayed", true);
  if (!pref || pref === "notDisplayed") return null;
  const config = getBudgetFieldsConfig().find((f) => f.budgetField === pref);
  const raw = fieldValue(ctx);
  if (raw == null || raw === "") return null;
  const formatted = config?.isPercentage
    ? `${raw}%`
    : config?.budgetValueFormatter
      ? config.budgetValueFormatter.format(raw as number)
      : String(raw);
  return renderIconValueLine(ctx, formatted, "truncate");
};

export const renderResourceIcon = (ctx: KanbanCardFieldRenderContext) => {
  const iconValue = fieldValue(ctx) as string | undefined;
  if (!iconValue) return null;
  const teamColor = ctx.record?.teamColor as string | undefined;
  return (
    <i
      className={cn("fa text-sm", getFontAwesomeClassName(iconValue))}
      style={teamColor ? { color: teamColor } : undefined}
      aria-hidden
    />
  );
};

export const renderBlackout = (ctx: KanbanCardFieldRenderContext) => {
  if (!ctx.record?.hideAgenda) return null;
  return <i className="fa-regular fa-lightbulb-slash text-[#ff4646] text-sm" aria-hidden />;
};

// --- Catalog helpers ---

export const getKanbanCardFieldCatalog = (entityType: string): KanbanCardFieldDefinition[] =>
  getCatalogMap()[entityType as EntityTypeValue] ?? [];

export const getCatalogFieldById = (entityType: string, fieldId: string): KanbanCardFieldDefinition | undefined =>
  getKanbanCardFieldCatalog(entityType).find((f) => f.id === fieldId);

export const buildKanbanCardPreviewRecord = (entityType: string): Record<string, unknown> => {
  const record: Record<string, unknown> = { id: "preview" };
  for (const field of getKanbanCardFieldCatalog(entityType)) {
    if (!field.mockConfig) continue;
    setNestedRecordValue(record, field.recordField ?? field.id, field.mockConfig.value);
  }
  return record;
};

export const getConfigurableKanbanFields = (catalog: KanbanCardFieldDefinition[]) =>
  catalog.filter((f) => !f.alwaysVisible);

export const getAlwaysVisibleKanbanFields = (catalog: KanbanCardFieldDefinition[]) =>
  catalog.filter((f) => f.alwaysVisible);

export const getSettingsPinnedKanbanFields = (catalog: KanbanCardFieldDefinition[]) =>
  catalog.filter((f) => f.alwaysVisible && f.showInSettings === true);

export const getKanbanCardFieldDefaults = (entityType: string): string[] =>
  getConfigurableKanbanFields(getKanbanCardFieldCatalog(entityType))
    .filter((f) => f.defaultVisible)
    .map((f) => f.id)
    .slice(0, maxKanbanVisibleFields);

export const getAvailableKanbanFields = (catalog: KanbanCardFieldDefinition[], visibleIds: string[]) =>
  getConfigurableKanbanFields(catalog).filter((f) => !visibleIds.includes(f.id));

export const addKanbanVisibleField = (
  current: string[],
  fieldId: string,
  max: number = maxKanbanVisibleFields
): string[] => {
  if (current.includes(fieldId) || current.length >= max) return current;
  return [...current, fieldId];
};

export const removeKanbanVisibleField = (current: string[], fieldId: string): string[] =>
  current.filter((id) => id !== fieldId);

export const reorderKanbanVisibleField = (
  current: string[],
  movedId: string,
  beforeId: string | null
): string[] => {
  const next = [...current];
  const movedIndex = next.indexOf(movedId);
  if (movedIndex === -1) return current;
  const [removed] = next.splice(movedIndex, 1);
  if (beforeId === null) {
    next.push(removed);
  } else {
    const beforeIndex = next.indexOf(beforeId);
    if (beforeIndex === -1) next.push(removed);
    else next.splice(beforeIndex, 0, removed);
  }
  return next;
};

export const replaceKanbanVisibleField = (
  current: string[],
  newFieldId: string,
  replaceFieldId: string,
  max: number = maxKanbanVisibleFields
): string[] => {
  if (current.includes(newFieldId)) return current;
  const replaceIndex = current.indexOf(replaceFieldId);
  if (replaceIndex === -1) return current;
  const next = [...current];
  next[replaceIndex] = newFieldId;
  return next.slice(0, max);
};
